/* eslint-disable react/no-unstable-nested-components */

import type { IconName } from '@fortawesome/fontawesome-svg-core'
import { IM, useLanguage, useTheme } from '@infominds/react-native-components'
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { createStackNavigator } from '@react-navigation/stack'
import * as React from 'react'
import { Alert, Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { TouchableOpacity } from '../components/Themed'
import Colors from '../constants/Colors'
import Layout from '../constants/Layout'
import DelegateScreen from '../screens/DelegateScreen'
import DetailScreen from '../screens/DetailScreen'
import HistoryScreen from '../screens/HistoryScreen'
import PreviewScreen from '../screens/PreviewScreen'
import TabArchiveScreen from '../screens/TabArchiveScreen'
import TabHomeScreen from '../screens/TabHomeScreen'
import TabSettingsScreen from '../screens/TabSettingsScreen'
import { BottomTabParamList, TabArchiveParamList, TabHomeParamList, TabSettingsParamList } from '../types'

const BottomTab = createBottomTabNavigator<BottomTabParamList>()

export default function BottomTabNavigator() {
  const { colorScheme } = useTheme()
  const { i18n } = useLanguage()

  return (
    <BottomTab.Navigator
      initialRouteName="Home"
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: Colors[colorScheme].tint,
      }}>
      <BottomTab.Screen
        name="Home"
        component={TabHomeNavigator}
        options={{
          title: i18n.t('ID_HOME'),
          tabBarIcon: ({ color }) => <TabBarIcon name="home" color={color} />,
        }}
      />
      <BottomTab.Screen
        name="Archive"
        component={TabTwoNavigator}
        options={{
          title: i18n.t('ID_ARCHIVE'),
          tabBarIcon: ({ color }) => <TabBarIcon name="archive" color={color} />,
        }}
      />
      <BottomTab.Screen
        name="Settings"
        component={TabSettingsNavigator}
        options={{
          title: i18n.t('ID_EINSTELLUNGEN'),
          tabBarIcon: ({ color }) => <TabBarIcon name="gear" color={color} />,
        }}
      />
    </BottomTab.Navigator>
  )
}

function TabBarIcon(props: { name: IconName; color: string }) {
  // eslint-disable-next-line react-native/no-inline-styles
  if (Layout.isSmallDevice) return <IM.Icon size={28} style={{ marginBottom: -3 }} icon={['far', props.name]} {...props} />
  else return <IM.Icon size={28} icon={['far', props.name]} {...props} />
}

const TabHomeStack = createStackNavigator<TabHomeParamList>()

function TabHomeNavigator() {
  const { left } = useSafeAreaInsets()
  const { i18n } = useLanguage()

  return (
    <TabHomeStack.Navigator initialRouteName="TabHomeScreen">
      <TabHomeStack.Screen
        name="TabHomeScreen"
        component={TabHomeScreen}
        options={{
          headerTitle: i18n.t('ID_BILLS'),
          headerTintColor: Colors.tint,
          headerTitleContainerStyle: { left: Platform.OS !== 'ios' ? left + 12 : undefined },
          headerLeft: () => <></>,
          headerRight: () => (
            <TouchableOpacity
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                backgroundColor: 'rgba(52, 52, 52, 0)',
              }}
              onPress={() => Alert.alert('Info', i18n.t('ID_INFO_HOME'))}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <IM.Icon size={28} color={Colors.tintIconDisabled} style={{ marginRight: 10 }} icon={['far', 'circle-info']} />
            </TouchableOpacity>
          ),
        }}
      />
      <TabHomeStack.Screen
        name="PreviewScreen"
        component={PreviewScreen}
        options={{ headerTitle: i18n.t('PREVIEW'), headerTintColor: Colors.tint }}
      />
      <TabHomeStack.Screen name="DetailScreen" component={DetailScreen} options={{ headerTitle: i18n.t('DETAIL'), headerTintColor: Colors.tint }} />
      <TabHomeStack.Screen
        name="HistoryScreen"
        component={HistoryScreen}
        options={{ headerTitle: i18n.t('HISTORY'), headerTintColor: Colors.tint }}
      />
      <TabHomeStack.Screen
        name="DelegateScreen"
        component={DelegateScreen}
        options={{ headerTitle: i18n.t('DELEGATE'), headerTintColor: Colors.tint }}
      />
    </TabHomeStack.Navigator>
  )
}

const TabArchiveStack = createStackNavigator<TabArchiveParamList>()

function TabTwoNavigator() {
  const { left } = useSafeAreaInsets()
  const { i18n } = useLanguage()

  return (
    <TabArchiveStack.Navigator>
      <TabArchiveStack.Screen
        name="TabArchiveScreen"
        component={TabArchiveScreen}
        options={{
          headerTitle: i18n.t('ID_ARCHIVE'),
          headerTintColor: Colors.tint,
          headerTitleContainerStyle: { left: Platform.OS !== 'ios' ? left + 12 : undefined },
          headerLeft: () => <></>,
          headerRight: () => (
            <TouchableOpacity
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                backgroundColor: 'rgba(52, 52, 52, 0)',
              }}
              onPress={() => Alert.alert('Info', i18n.t('ID_INFO_ARCHIVE'))}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <IM.Icon size={28} color={Colors.tintIconDisabled} style={{ marginRight: 10 }} icon={['far', 'circle-info']} />
            </TouchableOpacity>
          ),
        }}
      />
      <TabArchiveStack.Screen
        name="PreviewScreen"
        component={PreviewScreen}
        options={{ headerTitle: i18n.t('PREVIEW'), headerTintColor: Colors.tint }}
      />
      <TabArchiveStack.Screen
        name="HistoryScreen"
        component={HistoryScreen}
        options={{ headerTitle: i18n.t('HISTORY'), headerTintColor: Colors.tint }}
      />
    </TabArchiveStack.Navigator>
  )
}

const TabSettingsStack = createStackNavigator<TabSettingsParamList>()

function TabSettingsNavigator() {
  const { left } = useSafeAreaInsets()
  const { i18n } = useLanguage()

  return (
    <TabSettingsStack.Navigator>
      <TabSettingsStack.Screen
        name="TabSettingsScreen"
        component={TabSettingsScreen}
        options={{
          headerTitle: i18n.t('ID_EINSTELLUNGEN'),
          headerTitleContainerStyle: { left: Platform.OS !== 'ios' ? left + 12 : undefined },
          headerTintColor: Colors.tint,
          headerLeft: () => <></>,
        }}
      />
    </TabSettingsStack.Navigator>
  )
}
